import React, { useEffect, useState } from "react";
import SectionTitle from "../../Components/SectionTitle";
import { Link } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Image, Input, message, Select, Table } from "antd";
import { ArticleTableColumns, ArticleTableData } from "./types";
import { deleteArticle, filterArticles, markArticleAsEdited } from "./action";
import { result } from "lodash";
import DashPagination from "../../Components/DashPagination";
import { ICategory } from "../../types/Category";
import { getCategories } from "../Categories/action";
import DashModal from "../../Components/DashModal";
import AddNewArticle from "./Components/AddNewArticle";
import EditSEO from "./Components/EditSeo";
import { CLOUD_URL } from "../../lib/env";
import EditArticleImage from "./Components/EditImage";
import EditContent from "./Components/EditContent";
import ManageAttachments from "./Components/ManageAttachments";
import EditMainInfo from "./Components/EditMainInfo";
import ViewChanger from "../../Components/ViewChanger";
import ArticleCardViewer from "./Components/ArticleCardViewer";
import { getCounter } from "../../lib/functions";

const { Search } = Input;
type Props = {
  addNewArticle?: boolean;
  manageGallary?: boolean;
  editContent?: boolean;
  editImage?: boolean;
  editMainInfo?: boolean;
  editSeo?: boolean;
};

const Articles = (props: Props) => {
  const [data, setData] = useState<ArticleTableData[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [showTable, setShowTable] = React.useState(true);

  const handleExpand = (expanded: boolean, record: ArticleTableData) => {
    setExpandedRowKeys(expanded ? [record.id] : []);
  };
  const fetchCategories = async () => {
    const result = await getCategories();
    if (result) {
      setCategories(result);
    }
  };
  const fetchData = async () => {
    const articles = await filterArticles(pageSize, page, query, category);
    if (articles) {
      const appendResult = articles.map((item) => ({
        ...item,
        title: item.MainInfo.Title,
        author: item.MainInfo.Author,
        source_name: item.MainInfo.Source.Name,
        source_link: item.MainInfo.Source.Link,
        alt: item.MainImage.Alt,
        image: item.MainImage.FilePath,
        category: item.MainInfo.Category,
        keywords: item.Seo.Keywords,
        description: item.Seo.Description,
        createdAt: new Date(item.Infos.CreatedAt).toLocaleString(),
        updatedAt: item.Infos.UpdatedAt,
        onMainNews: item.Infos.OnMainNews,
        user:
          item.Infos.CreatedBy.MainInfo.FirstName +
          " " +
          item.Infos.CreatedBy.MainInfo.LastName,
        published: item.Infos.Published,
      }));
      setData(appendResult);
    }
  };
  const handleDelete = async (id: string) => {
    const result = await deleteArticle(id);
    if (result) {
      message.success("Article deleted successfully");
      fetchData();
    }
  };

  const handleUpdate = async (id: string) => {
    const result = await markArticleAsEdited(id);
    if (result) {
      message.success("Article updated successfully");
      fetchData();
    }
  };
  useEffect(() => {
    fetchCategories();
    const fetchPlayersCount = async () => {
      const count = await getCounter("Articles");
      if (count) {
        setTotal(count);
      }
    };
    fetchPlayersCount();
  }, []);
  useEffect(() => {
    fetchData();
  }, [page, pageSize, query, category]);

  const columns = ArticleTableColumns(
    page,
    pageSize,
    handleDelete,
    handleUpdate,
    fetchData
  );
  return (
    <div className=" space-y-5">
      <SectionTitle title="Articles" />
      <div className="flex flex-row justify-between">
        <Link to={`/articles/add?page=${page + 1}&pageSize=${pageSize}`}>
          <Button type="primary" icon={<PlusOutlined />}>
            Add New Article
          </Button>
        </Link>
        <div className="flex flex-row gap-2">
          <Search
            style={{ width: 300 }}
            onSearch={(value) => setQuery(value)}
            allowClear
            placeholder="Search..."
          />
          <Select
            placeholder={"Select Category ..."}
            allowClear
            value={category}
            onChange={setCategory}
            style={{ width: 300 }}
            options={categories.map((item) => ({
              label: item.MainInfo.Title,
              value: item.id,
            }))}
          />
          <ViewChanger view={showTable} setView={setShowTable} />
        </div>
      </div>

      {showTable ? (
        <Table<ArticleTableData>
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey="id"
          pagination={false}
          bordered
          expandable={{
            expandedRowRender: (record) => (
              <Image
                width={200}
                alt={record.alt}
                src={CLOUD_URL + "/" + record.image}
              />
            ),
            rowExpandable: () => true,
            onExpand: handleExpand,
            expandedRowKeys,
          }}
        />
      ) : (
        <ArticleCardViewer
          articles={data}
          page={page}
          pageSize={pageSize}
          handleRefresh={fetchData}
        />
      )}

      <DashPagination
        page={page}
        setPage={setPage}
        totalNumber={total}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
      {props.addNewArticle && (
        <DashModal
          width={700}
          title="Add New Article"
          cancleLink={`/articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <AddNewArticle onArticleAdded={fetchData} />
        </DashModal>
      )}
      {props.editSeo && (
        <DashModal
          width={700}
          title="Edit SEO"
          cancleLink={`/articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditSEO onSeoEdit={fetchData} />
        </DashModal>
      )}
      {props.editImage && (
        <DashModal
          width={700}
          title="Edit Image"
          cancleLink={`/articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditArticleImage onImageEdit={fetchData} />
        </DashModal>
      )}
      {props.editContent && (
        <DashModal
          width={1400}
          title="Edit Content"
          cancleLink={`/articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditContent onContentChange={fetchData} />
        </DashModal>
      )}
      {props.manageGallary && (
        <DashModal
          width={1200}
          title="Manage Attachments"
          cancleLink={`/articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <ManageAttachments onAttachmentsChange={fetchData} />
        </DashModal>
      )}
      {props.editMainInfo && (
        <DashModal
          width={850}
          title="Edit Main Info"
          cancleLink={`/articles?page=${page + 1}&pageSize=${pageSize}`}
        >
          <EditMainInfo onMainInfoEdit={fetchData} />
        </DashModal>
      )}
    </div>
  );
};

export default Articles;
